import { useEffect, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import { userPermissions } from '@/common/constants'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import commonServices from '@/common/services/services'
import { settingsTypes } from '@/configuration/constants'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode, getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import { getModifyThemePayload, isFormValid } from '@/configuration/pages/ConfigurationPage/ThemeConfiguration/helpers'
import configurationServices from '@/configuration/services/services'

import useThemeColors from '../useThemeColors/useThemeColors'
import useThemeImages from '../useThemeImages/useThemeImages'
import { servicesByConfigurationMode } from './constants'

const useThemeConfiguration = ({
  merchantId,
  returnPortalId,
  onSaveSettingsType,
  settingsTypeHasChanges,
  isUnifiedDashboard,
  setToastInfo,
  updateContext,
  t,
  settingsType,
  revertSettingsType,
  onSaveError,
}) => {
  const { editBranding } = userPermissions
  const { hasPermission } = useUserPermissions()

  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })

  const { fetchData: modifyTheme, loading: applyingTheme } = useFetch(services.modifyTheme, {
    doInitialRequest: false,
  })

  const { fetchData: getShopInfo, loading: gettingShopInfo } = useFetch(commonServices.getShopInfo, {
    doInitialRequest: false,
  })

  const {
    data: merchantTheme,
    loading: loadingMerchantTheme,
    fetchData: refetchMerchantTheme,
  } = useFetch(() => configurationServices.getMerchantTheme({ merchantId }), {
    doInitialRequest: configurationMode === configurationTypes.merchant,
  })

  const {
    data: returnPortalTheme,
    loading: loadingReturnPortalTheme,
    fetchData: refetchReturnPortalTheme,
  } = useFetch(() => configurationServices.getPortalTheme({ returnPortalId }), {
    doInitialRequest: configurationMode === configurationTypes.returnsPortal,
  })

  const [currentMode, setCurrentMode] = useState(configurationMode)

  useEffect(() => {
    if (currentMode && configurationMode !== currentMode) {
      if (configurationMode === configurationTypes.merchant) {
        refetchMerchantTheme()
      } else {
        refetchReturnPortalTheme()
      }
    }
    setCurrentMode(configurationMode)
  }, [configurationMode, currentMode, refetchMerchantTheme, refetchReturnPortalTheme])

  const themeData = configurationMode === configurationTypes.merchant ? merchantTheme : returnPortalTheme

  const { colors, changeColor, hasColorChanges, setHasColorChanges, revertColorChanges } = useThemeColors(themeData)
  const {
    images,
    deleteImage,
    changeImage,
    changeLink,
    newThemeImages,
    hasImageChanges,
    setHasImageChanges,
    revertImageChanges,
  } = useThemeImages(themeData)

  const updateSelectedStore = shopInfo => {
    const selectedStore = localStorage.selectedStore
      ? shopInfo.stores.find(store => store.id === JSON.parse(localStorage.selectedStore).id)
      : shopInfo.stores[0]

    updateContext(info => ({ ...info, ...shopInfo, selectedStore }))
  }

  const handleOnSave = async () => {
    try {
      if (!isUnifiedDashboard && settingsTypeHasChanges) {
        await onSaveSettingsType()
      }

      const newTheme = {
        images: newThemeImages(),
        colors,
      }

      const themePayload = getModifyThemePayload(newTheme)

      await modifyTheme({ ...entityId, theme: themePayload })

      if (configurationMode === configurationTypes.merchant) {
        await refetchMerchantTheme()
      } else {
        await refetchReturnPortalTheme()
        const shopInfo = await getShopInfo(null)
        updateSelectedStore(shopInfo)
      }

      setHasColorChanges(false)
      setHasImageChanges(false)

      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      onSaveError?.(error)
    }
  }

  const onCancelChanges = () => {
    revertColorChanges()
    revertImageChanges()
    revertSettingsType()
  }

  const hasChanges = hasColorChanges || hasImageChanges

  const shouldShowHasChanges = hasPermission(editBranding) && (hasChanges || settingsTypeHasChanges)

  const isDisabledByGlobalSettings = !isUnifiedDashboard && settingsType === settingsTypes.global

  const newTheme = {
    images: newThemeImages(),
    colors,
  }

  const isThemeValid = isFormValid(newTheme)

  const trackingPreviewUrl =
    configurationMode === configurationTypes.merchant
      ? merchantTheme?.preview?.tracking_url
      : returnPortalTheme?.preview?.tracking_url

  const loadingTheme =
    configurationMode === configurationTypes.merchant
      ? loadingMerchantTheme || !merchantTheme
      : loadingReturnPortalTheme || !returnPortalTheme

  return {
    handleOnSave,
    applyingTheme,
    gettingShopInfo,
    merchantTheme,
    loadingTheme,
    onCancelChanges,
    hasChanges,
    shouldShowHasChanges,
    colors,
    changeColor,
    images,
    deleteImage,
    changeImage,
    changeLink,
    isDisabledByGlobalSettings,
    isThemeValid,
    hasPermission,
    trackingPreviewUrl,
  }
}

export default useThemeConfiguration
